import {
  addClip,
  addProjectAudioTrack,
  clearUndo,
  deleteClip,
  deleteProjectIfPristine,
  duplicateClip,
  getClipsForProject,
  getProject,
  getProjectAudio,
  getProjectText,
  getSettings,
  getUndoSnapshot,
  listProjects,
  moveClip,
  removeProjectAudioTrack,
  saveProjectText,
  setLastOpenedProjectId,
  undoDeleteLastClip,
  updateClipThumbs,
  updateClipTrim,
  updateClipVolumes,
  updateProjectAudioTrack,
  type ClipVolumeSettings,
  type ProjectAudioTrackSettings,
} from './storage'
import { probeAudioFile } from './audio-import'
import { estimateExportCacheBytes } from './export/export-cache'
import { estimateStorageSpace, type StorageSpace } from './storage-space'
import type { GeneratedThumbs } from './thumbs'
import {
  NEW_PROJECT_ID,
  countWords,
  effectiveDurationMs,
  projectKind,
  type ClipId,
  type ClipRecord,
  type Project,
  type ProjectAudioRecord,
  type ProjectId,
  type ProjectKind,
} from './types'

export interface ProjectSummary extends Project {
  clipCount: number
  durationMs: number
  /** First available clip thumbnail, for the project slot background. */
  posterThumb: Blob | null
  /** Words in the body — the only size that means anything for text. */
  wordCount: number
  /** First line of the body, for the text-project card subtitle. */
  textPreview: string
}

export interface ProjectLoaderData {
  project: Project | null
  /** The project's kind, resolved once so pages never re-derive it. */
  kind: ProjectKind
  clips: ClipRecord[]
  /** The project's body copy ('' when it has none). */
  text: string
  /** Background-audio track played under the clips (null when none is set). */
  audio: ProjectAudioRecord | null
  canUndo: boolean
  onboardingDismissed: boolean
  /** True when the one-time Kody Video Plus purchase is unlocked. */
  watermarkRemoved: boolean
  /**
   * Plus opt-in: keep the Kody mark on exports after purchase (default off).
   */
  keepWatermark: boolean
  /** Device storage estimate (null when the API is unavailable). */
  storage: StorageSpace | null
  /** Opt-in: tag new clips with device location. */
  locationTaggingEnabled: boolean
  error: string | null
}

export interface HomeLoaderData {
  /** Projects of the requested kind only — one tab's worth. */
  projects: ProjectSummary[]
  /** How many projects exist in each tab, for the tab badges. */
  countsByKind: Record<ProjectKind, number>
  storage: StorageSpace | null
  /** Bytes held by cached export files (recoverable last export, scratch). */
  exportCacheBytes: number
  /** True when the one-time Kody Video Plus purchase is unlocked. */
  plus: boolean
  /** Home "Watch the tour" card dismissed (first-timer teaser). */
  tourCardDismissed: boolean
}

export async function loadHomePage(kind: ProjectKind = 'video'): Promise<HomeLoaderData> {
  const [live, storage, exportCacheBytes, settings] = await Promise.all([
    loadLiveProjects(),
    estimateStorageSpace(),
    estimateExportCacheBytes(),
    getSettings(),
  ])
  const countsByKind: Record<ProjectKind, number> = { video: 0, voice: 0, text: 0 }
  for (const project of live) countsByKind[projectKind(project)] += 1
  return {
    projects: await Promise.all(
      live.filter((project) => projectKind(project) === kind).map(summarizeProject),
    ),
    countsByKind,
    storage,
    exportCacheBytes,
    plus: settings.watermarkRemoved === true,
    tourCardDismissed: settings.tourCardDismissed === true,
  }
}

/**
 * Every project that still exists, in stable slot order.
 *
 * Exiting a project still in its default state (no clips, default name, no
 * music, no body) must leave nothing behind, just like backing out of
 * /project/new. Every exit path lands back home, so such projects are
 * silently deleted before the cards render.
 */
async function loadLiveProjects(): Promise<Project[]> {
  const all = await listProjects()
  const list: Project[] = []
  for (const project of all) {
    if (project.clipIds.length === 0 && (await deleteProjectIfPristine(project.id))) continue
    list.push(project)
  }
  // Creation order — cards must not shuffle every time you open a project.
  list.sort((a, b) => a.createdAt - b.createdAt)
  return list
}

async function summarizeProject(project: Project): Promise<ProjectSummary> {
  const kind = projectKind(project)
  // Text projects never have clips; skipping the reads keeps the Text tab
  // instant no matter how many notes it holds.
  const clips = kind === 'text' ? [] : await getClipsForProject(project.id)
  const durationMs = clips.reduce((sum, clip) => sum + effectiveDurationMs(clip), 0)
  // Prefer any clip with a high-res poster over an older thumbs-only one.
  const withPoster = clips.find((clip) => clip.poster)
  const withThumb = clips.find((clip) => clip.thumbs && clip.thumbs.length > 0)
  const body = kind === 'video' ? '' : ((await getProjectText(project.id))?.body ?? '')
  return {
    ...project,
    clipCount: clips.length,
    durationMs,
    posterThumb: withPoster?.poster ?? withThumb?.thumbs?.[0] ?? null,
    wordCount: countWords(body),
    textPreview: body.trim().split('\n')[0]?.slice(0, 80) ?? '',
  }
}

/** Back-compat helper for callers that only want the video-era summaries. */
export async function loadHomeProjects(kind: ProjectKind = 'video'): Promise<ProjectSummary[]> {
  const live = await loadLiveProjects()
  return Promise.all(live.filter((project) => projectKind(project) === kind).map(summarizeProject))
}

export async function loadProjectPage(
  projectId: ProjectId,
  /** Kind for a not-yet-created "/project/new" — ignored for stored ones,
   * whose own record is the authority. */
  newProjectKind: ProjectKind = 'video',
): Promise<ProjectLoaderData> {
  try {
    // A "new" project lives only in the URL until the first clip is
    // recorded — nothing is persisted, so backing out leaves nothing behind.
    if (projectId === NEW_PROJECT_ID) {
      const [projects, settings, storage] = await Promise.all([
        listProjects(newProjectKind),
        getSettings(),
        estimateStorageSpace(),
      ])
      const now = Date.now()
      return {
        project: {
          id: NEW_PROJECT_ID,
          name: `Project ${projects.length + 1}`,
          createdAt: now,
          updatedAt: now,
          clipIds: [],
          kind: newProjectKind,
        },
        kind: newProjectKind,
        clips: [],
        text: '',
        audio: null,
        canUndo: false,
        onboardingDismissed: settings.onboardingDismissed,
        watermarkRemoved: settings.watermarkRemoved === true,
        keepWatermark: settings.keepWatermark === true,
        storage,
        locationTaggingEnabled: settings.locationTaggingEnabled === true,
        error: null,
      }
    }

    const [project, clips, audio, text, undo, settings, storage] = await Promise.all([
      getProject(projectId),
      getClipsForProject(projectId),
      getProjectAudio(projectId),
      getProjectText(projectId),
      getUndoSnapshot(projectId),
      getSettings(),
      estimateStorageSpace(),
    ])
    if (!project) {
      return {
        project: null,
        kind: newProjectKind,
        clips: [],
        text: '',
        audio: null,
        canUndo: false,
        onboardingDismissed: settings.onboardingDismissed,
        watermarkRemoved: settings.watermarkRemoved === true,
        keepWatermark: settings.keepWatermark === true,
        storage,
        locationTaggingEnabled: settings.locationTaggingEnabled === true,
        error: 'Project not found',
      }
    }
    await setLastOpenedProjectId(projectId)
    const kind = projectKind(project)
    // Backfill filmstrip thumbnails and the persisted audio-normalization
    // measurement for clips that lack them. Serially — Android caps
    // concurrent video decoders hard, and this normally touches at most
    // the clip that was just recorded.
    // Lazy: thumbs / clip-audio-peak → export/shared → mediabunny. Home
    // never hits this path. Voice takes have no video track to poster and
    // no per-clip normalization to measure, so they skip it entirely.
    let hydrated: ClipRecord[] = clips
    if (kind === 'video') {
      const { ensureClipThumbs } = await import('./thumbs')
      const { ensureClipAudioPeak } = await import('./clip-audio-peak')
      hydrated = []
      for (const clip of clips) {
        hydrated.push(await ensureClipAudioPeak(await ensureClipThumbs(clip)))
      }
    }
    return {
      project,
      kind,
      clips: hydrated,
      text: text?.body ?? '',
      audio: audio ?? null,
      canUndo: !!undo,
      onboardingDismissed: settings.onboardingDismissed,
      watermarkRemoved: settings.watermarkRemoved === true,
      keepWatermark: settings.keepWatermark === true,
      storage,
      locationTaggingEnabled: settings.locationTaggingEnabled === true,
      error: null,
    }
  } catch (err) {
    return {
      project: null,
      kind: newProjectKind,
      clips: [],
      text: '',
      audio: null,
      canUndo: false,
      onboardingDismissed: true,
      watermarkRemoved: false,
      keepWatermark: false,
      storage: null,
      locationTaggingEnabled: false,
      error: err instanceof Error ? err.message : 'Failed to load project',
    }
  }
}

export async function appendRecording(
  projectId: ProjectId,
  input: {
    blob: Blob
    mimeType: string
    durationMs: number
    /** Default trim-out (recordings end their kept range at the release
     * point; the media itself runs a stop-grace longer). */
    trimEndMs?: number
    width?: number
    height?: number
    lat?: number
    lng?: number
    locationAccuracyM?: number
  },
  options?: {
    /** Poster/thumb captured from the live preview at take end — persisting
     * them here means the loader backfill never has to decode the fresh
     * blob behind the live camera (the post-take black flash). */
    capturedThumbs?: GeneratedThumbs | null
  },
): Promise<ClipRecord> {
  const clip = await addClip({
    projectId,
    blob: input.blob,
    mimeType: input.mimeType,
    durationMs: input.durationMs,
    trimEndMs: input.trimEndMs,
    width: input.width,
    height: input.height,
    lat: input.lat,
    lng: input.lng,
    locationAccuracyM: input.locationAccuracyM,
  })
  if (options?.capturedThumbs) {
    // Best-effort: on a (rare) persistence failure the loader backfill
    // still generates thumbs — one black flash beats missing artwork. The
    // returned record is NOT decorated in memory: callers revalidate from
    // the store, and claiming unsaved thumbs would mask exactly that
    // fallback.
    await updateClipThumbs(clip.id, options.capturedThumbs).catch(() => undefined)
  }
  await clearUndo(projectId)
  // Thumbnails are generated by the loader backfill on the next revalidation.
  return clip
}

/**
 * Save a finished voice take. Unlike a camera take there is nothing to
 * poster, measure or trim back — the joiner handles the seams — so this is
 * just the blob and its measured length.
 */
export async function appendVoiceTake(
  projectId: ProjectId,
  take: { blob: Blob; mimeType: string; durationMs: number },
): Promise<ClipRecord> {
  const clip = await addClip({
    projectId,
    blob: take.blob,
    mimeType: take.mimeType,
    durationMs: take.durationMs,
  })
  await clearUndo(projectId)
  return clip
}

/** Persist a project's body copy (a text note, or a voice project's notes). */
export async function saveProjectBody(projectId: ProjectId, body: string): Promise<void> {
  await saveProjectText(projectId, body)
}

/** Notes typed alongside a voice recording, published as the entry body. */
export async function saveVoiceNotes(projectId: ProjectId, body: string): Promise<void> {
  await saveProjectText(projectId, body)
}

export async function removeClip(clipId: ClipId): Promise<void> {
  await deleteClip(clipId)
}

export async function undoLastDelete(projectId: ProjectId): Promise<ClipRecord | null> {
  return undoDeleteLastClip(projectId)
}

export async function duplicateSelectedClip(clipId: ClipId): Promise<ClipRecord> {
  return duplicateClip(clipId)
}

export async function moveSelectedClip(
  projectId: ProjectId,
  clipId: ClipId,
  direction: 'left' | 'right',
): Promise<void> {
  await moveClip(projectId, clipId, direction)
}

export async function trimClip(
  clipId: ClipId,
  trimStartMs: number,
  trimEndMs: number,
): Promise<void> {
  await updateClipTrim(clipId, trimStartMs, trimEndMs)
}

/** Append a picked audio file to the project's background-music playlist. */
export async function addProjectAudioFromFile(
  file: File,
  ensureProjectId: () => Promise<ProjectId>,
): Promise<ProjectAudioRecord> {
  // Probe first: a bad pick on /project/new must not create an empty project.
  const probed = await probeAudioFile(file)
  const projectId = await ensureProjectId()
  return addProjectAudioTrack({
    projectId,
    blob: probed.blob,
    mimeType: probed.mimeType,
    durationMs: probed.durationMs,
    name: probed.name,
  })
}

export async function removeAudioTrack(projectId: ProjectId, trackId: string): Promise<void> {
  await removeProjectAudioTrack(projectId, trackId)
}

/** Update one playlist track's playback settings (trim, level, fades). */
export async function setAudioTrackSettings(
  projectId: ProjectId,
  trackId: string,
  settings: ProjectAudioTrackSettings,
): Promise<void> {
  await updateProjectAudioTrack(projectId, trackId, settings)
}

/** Set a clip's volume levels (1 or null clears the stored override). */
export async function setClipVolumes(
  clipId: ClipId,
  volumes: ClipVolumeSettings,
): Promise<void> {
  await updateClipVolumes(clipId, volumes)
}

export {
  DEVICE_CLIP_ACCEPT,
  importDeviceClips,
  type DeviceClipImportResult,
} from './import-device-clips'
